import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://api.innoworx.ai/Stage';

const RESIDENTS_BASE_URL =
  process.env.REACT_APP_RESIDENTS_OWNERS_API_BASE_URL || API_BASE_URL;

const FACILITY_BASE_URL = API_BASE_URL;

const DUES_SITE_STORAGE_KEY = 'innoworx_dues_site_id';
const DUES_CACHE_STORAGE_KEY = 'innoworx_dues_site_cache';
const DUES_LIST_FORBIDDEN_KEY = 'innoworx_dues_list_forbidden';

const FACILITY_DUES_PATH = '/facility/dues';

const getStorage = () => {
  if (typeof localStorage !== 'undefined') return localStorage;
  if (typeof sessionStorage !== 'undefined') return sessionStorage;
  return null;
};

const isListForbidden = () => {
  if (typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(DUES_LIST_FORBIDDEN_KEY) === '1';
};

const setListForbidden = (forbidden) => {
  if (typeof sessionStorage === 'undefined') return;
  if (forbidden) sessionStorage.setItem(DUES_LIST_FORBIDDEN_KEY, '1');
  else sessionStorage.removeItem(DUES_LIST_FORBIDDEN_KEY);
};

export const getDuesSiteId = () =>
  typeof sessionStorage !== 'undefined'
    ? sessionStorage.getItem(DUES_SITE_STORAGE_KEY) || ''
    : '';

export const setDuesSiteId = (siteId) => {
  if (typeof sessionStorage === 'undefined') return;
  if (siteId) sessionStorage.setItem(DUES_SITE_STORAGE_KEY, siteId);
  else sessionStorage.removeItem(DUES_SITE_STORAGE_KEY);
};

const readDuesCache = () => {
  const storage = getStorage();
  if (!storage) return {};
  try {
    const raw = storage.getItem(DUES_CACHE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const writeDuesCache = (cache) => {
  const storage = getStorage();
  if (!storage) return;
  storage.setItem(DUES_CACHE_STORAGE_KEY, JSON.stringify(cache));
};

export const cacheDueForSite = (siteId, due) => {
  if (!siteId || !due) return;
  const id = due.due_id || due.id;
  if (!id) return;

  const cache = readDuesCache();
  const list = Array.isArray(cache[siteId]) ? [...cache[siteId]] : [];
  const entry = {
    ...due,
    due_id: due.due_id || id,
    site_id: due.site_id || siteId,
  };
  const idx = list.findIndex((d) => String(d.due_id || d.id) === String(id));
  if (idx >= 0) list[idx] = entry;
  else list.unshift(entry);
  cache[siteId] = list.slice(0, 100);
  writeDuesCache(cache);
};

export const getCachedDuesForSite = (siteId) => {
  if (!siteId) return [];
  const cache = readDuesCache();
  return Array.isArray(cache[siteId]) ? cache[siteId] : [];
};

export const removeDueFromSiteCache = (siteId, dueId) => {
  if (!siteId || !dueId) return;
  const cache = readDuesCache();
  const list = Array.isArray(cache[siteId]) ? cache[siteId] : [];
  const next = list.filter(
    (d) => String(d.due_id || d.id) !== String(dueId)
  );
  if (next.length === list.length) return;
  cache[siteId] = next;
  writeDuesCache(cache);
};

const mergeDuesById = (primary = [], secondary = []) => {
  const map = new Map();
  [...secondary, ...primary].forEach((due) => {
    const id = due?.due_id || due?.id;
    if (id) map.set(String(id), due);
  });
  return Array.from(map.values());
};

const parseApiErrorMessage = (decoded, status) => {
  if (!decoded) return `Request failed (${status})`;
  if (typeof decoded === 'string') return decoded;
  return (
    decoded.message ||
    decoded.error ||
    decoded.detail ||
    (Array.isArray(decoded.errors) ? decoded.errors.join(', ') : null) ||
    decoded.raw ||
    `Request failed (${status})`
  );
};

export const normalizeDuesListResponse = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.dues)) return data.dues;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.data)) return data.data;
  return [];
};

const getAuthToken = async () => {
  const { tokens } = await fetchAuthSession();
  const token = tokens?.idToken?.toString()?.trim();
  return token || null;
};

/** Facility APIs often require tenant_id (JWT claim and/or query). */
const getTenantId = async () => {
  const { tokens } = await fetchAuthSession();
  const payload = tokens?.idToken?.payload || {};
  const fromToken = payload['custom:tenant_id'] ?? payload.tenant_id;
  if (fromToken != null && fromToken !== '') return String(fromToken);
  if (process.env.REACT_APP_DEFAULT_TENANT_ID != null) {
    return String(process.env.REACT_APP_DEFAULT_TENANT_ID);
  }
  return null;
};

const withTenantQuery = async (query = {}) => {
  const next = { ...query };
  const tenantId = await getTenantId();
  if (tenantId && !next.tenant_id) next.tenant_id = tenantId;
  return next;
};

const withTenantBody = async (body = {}) => {
  const tenantId = await getTenantId();
  if (!tenantId || body.tenant_id) return body;
  return { ...body, tenant_id: tenantId };
};

const readResponseBody = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
};

const requestJson = async (
  path,
  { method = 'GET', query, body, baseUrl = RESIDENTS_BASE_URL, logError = true } = {}
) => {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required. Please sign in again.');

  const base = baseUrl.replace(/\/+$/, '');
  const url = `${base}${path}${query ? `?${new URLSearchParams(query).toString()}` : ''}`;

  const headers = { Authorization: `Bearer ${token}` };
  if (method !== 'GET' && body) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, {
    method,
    headers,
    ...(body && method !== 'GET' ? { body: JSON.stringify(body) } : {}),
  });

  const decoded = await readResponseBody(response);

  if (!response.ok) {
    const message = parseApiErrorMessage(decoded, response.status);

    const err = new Error(message);
    err.status = response.status;
    err.statusText = response.statusText;
    err.url = url;
    err.responseData = decoded;

    if (logError) {
      loggingService
        .logError('api_error_dues', `Dues API error: ${method} ${path}`, err, {
          method,
          path,
          status: response.status,
        })
        .catch(() => {});
    }

    throw err;
  }

  return decoded;
};

const requestFacilityList = (query) =>
  requestJson(FACILITY_DUES_PATH, {
    method: 'GET',
    query,
    baseUrl: FACILITY_BASE_URL,
    logError: false,
  });

const findDueInList = (list, dueId) =>
  list.find((d) => String(d.due_id || d.id) === String(dueId));

export const duesApi = {
  /** Facility: create flat-level due — POST /facility/dues */
  createFlatDue: async (payload) => {
    const res = await requestJson(FACILITY_DUES_PATH, {
      method: 'POST',
      body: await withTenantBody(payload),
      baseUrl: FACILITY_BASE_URL,
    });
    const due = res?.due || res;
    if (payload?.site_id && due) {
      cacheDueForSite(payload.site_id, {
        ...due,
        site_id: payload.site_id,
        flat_id: due.flat_id || payload.flat_id,
      });
    }
    return res;
  },

  /**
   * Facility: list flat dues for a site (supervisor web app).
   * GET /facility/dues?site_id=... — merges session cache when list is forbidden.
   */
  listBySite: async ({ site_id, limit = 50, forceRefresh = false } = {}) => {
    if (!site_id) {
      throw new Error('Select a site to load dues.');
    }

    const cached = getCachedDuesForSite(site_id);
    let apiList = [];
    let warning;

    const skipListFetch = !forceRefresh && isListForbidden();

    if (skipListFetch) {
      if (cached.length === 0) {
        throw new Error(
          'Cannot load dues: GET /facility/dues returned 403 for your role. Create a due to add one locally, or ask your admin to enable list access on /facility/dues.'
        );
      }
      warning =
        'Showing dues saved on this device. List API (GET /facility/dues) is not authorized for your account.';
    } else {
      try {
        const query = await withTenantQuery({
          site_id,
          limit: String(limit),
        });
        const res = await requestFacilityList(query);
        apiList = normalizeDuesListResponse(res);
        apiList.forEach((due) => cacheDueForSite(site_id, due));
        setListForbidden(false);
      } catch (err) {
        if (err.status === 403) setListForbidden(true);

        const apiMessage =
          err.responseData?.message ||
          err.responseData?.error ||
          err.message;

        if (cached.length === 0) {
          if (err.status === 403) {
            throw new Error(
              apiMessage ||
                'Access denied (403) on GET /facility/dues. Ask your admin to grant facility dues list permission for your role.'
            );
          }
          throw err;
        }
        warning =
          err.status === 403
            ? 'GET /facility/dues denied (403). Showing dues saved on this device only.'
            : apiMessage || 'Could not refresh dues from the server.';
      }
    }

    const dues = mergeDuesById(apiList, cached);
    return { dues, count: dues.length, warning };
  },

  /** Resident: list dues for flats the user belongs to — GET /dues/mine */
  listMine: async ({ site_id, limit = 50 } = {}) => {
    const query = {};
    if (site_id) query.site_id = site_id;
    if (limit) query.limit = String(limit);
    return requestJson('/dues/mine', { method: 'GET', query });
  },

  /**
   * Facility: get one due from GET /facility/dues list or session cache.
   */
  getDue: async (dueId) => {
    if (!dueId) throw new Error('Due ID is required');

    const site_id = getDuesSiteId();
    if (site_id) {
      const cached = findDueInList(getCachedDuesForSite(site_id), dueId);
      if (cached) {
        return {
          due: cached,
          payment: cached.payment ?? cached.latest_payment ?? null,
        };
      }

      if (!isListForbidden()) {
        try {
          const { dues } = await duesApi.listBySite({ site_id, limit: 100 });
          const match = findDueInList(dues, dueId);
          if (match) {
            return {
              due: match,
              payment: match.payment ?? match.latest_payment ?? null,
            };
          }
        } catch {
          /* list may also be forbidden */
        }
      }
    }

    throw new Error('Due not found or you do not have permission to view it.');
  },

  createPaymentIntent: async (dueId) =>
    requestJson(`/dues/${encodeURIComponent(dueId)}/payment-intent`, {
      method: 'POST',
      baseUrl: RESIDENTS_BASE_URL,
    }),

  confirmDummyPayment: async (dueId, payload) =>
    requestJson(`/dues/${encodeURIComponent(dueId)}/payment-confirm`, {
      method: 'POST',
      body: payload,
      baseUrl: RESIDENTS_BASE_URL,
    }),

  presignReceiptUpload: async (dueId, { file_name, content_type } = {}) =>
    requestJson(`/dues/${encodeURIComponent(dueId)}/receipt/presign`, {
      method: 'POST',
      body: {
        ...(file_name ? { file_name } : {}),
        ...(content_type ? { content_type } : {}),
      },
      baseUrl: RESIDENTS_BASE_URL,
    }),

  confirmReceipt: async (dueId, payload) =>
    requestJson(`/dues/${encodeURIComponent(dueId)}/receipt/confirm`, {
      method: 'POST',
      body: payload,
      baseUrl: RESIDENTS_BASE_URL,
    }),

  recordOfflinePayment: async (dueId, payload) =>
    requestJson(`${FACILITY_DUES_PATH}/${encodeURIComponent(dueId)}/payments`, {
      method: 'POST',
      body: payload,
      baseUrl: FACILITY_BASE_URL,
    }),

  /**
   * Facility: cancel (soft-delete) a due.
   * POST /facility/dues/{due_id}/cancel
   */
  cancelDue: async (dueId, { reason } = {}) => {
    if (!dueId) throw new Error('Due ID is required');
    return requestJson(`${FACILITY_DUES_PATH}/${encodeURIComponent(dueId)}/cancel`, {
      method: 'POST',
      body: {
        reason: reason || 'cancelled from facility app',
      },
      baseUrl: FACILITY_BASE_URL,
    });
  },

  uploadReceiptFileToPresignedUrl: async (uploadUrl, file, contentType) => {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType || file?.type || 'application/pdf',
      },
      body: file,
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(
        `Receipt upload failed (${response.status})${text ? `: ${text}` : ''}`
      );
    }
  },
};
